import { h, Fragment } from "preact";
import { useState } from "preact/hooks";

import UserActions from "../../../apps/data/user/UserActions";
import { PLATFORM, PlatformContext, PlatformContextMap } from "../../../foundation/enums";
import Link from "../../../foundation/router/Link";
import classname from "../../../foundation/utils/classname";
import IconArrowDown from "../Icons/IconArrowDown";
import IconCloseSmall from "../Icons/IconCloseSmall";
import Chip from "../Chip";
import Popover, { PopoverAlignment, PopoverPosition } from "../Popover";

import { Props } from "./PlatformSwitcherProps";

import styles from "./PlatformSwitcher.module.css";

function PlatformSwitcher({
    class: className,
    platform,
    platformSwitcherPromotionSeen
}: Props) {
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);

    const renderablePlatforms = Object.values(PlatformContextMap).filter(({ isDeprecated }) => !isDeprecated);

    function handlePopoverToggle() {
        setIsPopoverVisible(!isPopoverVisible);

        if (!platformSwitcherPromotionSeen) {
            UserActions.markPlatformSwitcherPromotionAsSeen();
        }
    }

    function handlePlatformClick(newPlatform: PLATFORM) {
        UserActions.setPlatform(newPlatform);
    }

    function renderTab(platformContext: PlatformContext) {
        return (
            <div
                class={classname(styles.popoverTab, "caption-1", {
                    [styles.selected]: platform === platformContext.id
                })}
                onClick={() => handlePlatformClick(platformContext.id)}>
                {platformContext.name}
            </div>
        );
    }

    function renderPopoverContent() {
        return (
            <>
                <p class={classname(styles.popoverTitle, "body-1")}>
                    View specs/assets for:
                </p>
                <div class={styles.popoverTabs}>
                    {renderablePlatforms.map(renderTab)}
                </div>
                <div class={styles.popoverDivider}/>
                <div class={styles.popoverItems}>
                    {PlatformContextMap[platform].items?.map(item => (
                        <p
                            key={item}
                            class={classname(styles.popoverItem, "body-1")}>
                            {item}
                        </p>
                    ))}
                </div>
            </>
        );
    }

    function renderPromotion() {
        if (platformSwitcherPromotionSeen) {
            return null;
        }

        const releaseDate = new Date("2025-05-30");
        const sixWeeksMs = 6 * 7 * 24 * 60 * 60 * 1000;
        const isNewChipVisible = new Date().getTime() < releaseDate.getTime() + sixWeeksMs;

        return (
            <>
                <div
                    class={styles.promotionCloseIconContainer}
                    onClick={UserActions.markPlatformSwitcherPromotionAsSeen}>
                    <IconCloseSmall/>
                </div>
                {isNewChipVisible && (
                    <Chip
                        class={styles.promotionChip}
                        text="New"/>
                )}
                <p class={classname(styles.promotionText, "body-1")}>
                    Switch platforms anytime—Zeplin will adjust specs and assets for you!
                </p>
                <Link
                    class={classname(styles.promotionLink, "body-1 medium")}
                    to="https://support.zeplin.io/en/articles/11458233-switching-platforms-to-access-specific-assets-and-specs"
                    isExternal={true}>
                    Learn more
                </Link>
                <div class={styles.divider}/>
            </>
        );
    }

    return (
        <div class={classname(styles.platformSwitcherWrapper, className)}>
            <Popover
                class={styles.popover}
                contentClass={styles.popoverContent}
                open={isPopoverVisible}
                content={renderPopoverContent()}
                position={PopoverPosition.Top}
                alignment={PopoverAlignment.End}
                offset={2}
                hideArrow
                hasOverlay
                onClose={handlePopoverToggle}>
                <div class={styles.platformSwitcher}>
                    {renderPromotion()}
                    <div class={styles.content}>
                        <span class={classname(styles.label, "body-1")}>
                            Platform:
                        </span>
                        <div
                            class={classname(styles.button, "caption-1")}
                            onClick={handlePopoverToggle}>
                            {PlatformContextMap[platform].name}
                            <IconArrowDown/>
                        </div>
                    </div>
                </div>
            </Popover>
        </div>
    );
}

export default PlatformSwitcher;
